import { useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { BifurcationItem } from "../../types/transactionHistory.types";

interface BifurcationSectionProps {
  bifurcation: Record<string, BifurcationItem> | null;
  loading: boolean;
}

function formatCurrency(amount: number | undefined | null): string {
  if (amount === undefined || amount === null || isNaN(amount)) return "₹ 0";
  return `₹ ${Math.round(amount).toLocaleString("en-IN")}`;
}

function formatTotal(amount: number | undefined | null): string {
  if (amount === undefined || amount === null || isNaN(amount)) return "₹0";
  return `₹${Math.round(amount).toLocaleString("en-IN")}`;
}

function getCategoryName(item: any, keyName?: string): string {
  const orderType = item?.orderType || item?.order_type || "";
  const directName = item?.name;

  // Specific returns mappings requested
  if (orderType === "online-return" || keyName === "onlineReturns") {
    return "Medicine Return · Online";
  }
  if (orderType === "offline-return" || keyName === "offlineReturns") {
    return "Medicine Return · In-Store";
  }

  // If directName is present and specific (not "Other")
  if (directName && directName !== "Other" && directName !== "other") {
    return directName;
  }

  // Map by orderType
  const typeMap: Record<string, string> = {
    "online-order": "Medicines · Online",
    "offline-order": "Medicines · In-Store",
    "appointment": "Appointments",
    "labtest-order": "Lab Test · Online",
    "offline-labtest": "Lab Test · In-Store",
    "online-return": "Medicine Return · Online",
    "offline-return": "Medicine Return · In-Store",
    "expense": "Other Expenses",
    "bank-deposit": "Bank Deposits",
  };

  if (typeMap[orderType]) return typeMap[orderType];

  // Map by key in bifurcation response
  const keyMap: Record<string, string> = {
    onlineOrders: "Medicines · Online",
    offlineOrders: "Medicines · In-Store",
    medicinesOnline: "Medicines · Online",
    medicinesInStore: "Medicines · In-Store",
    appointmentOrders: "Appointments",
    appointments: "Appointments",
    labtestOrders: "Lab Test · Online",
    labTestOnline: "Lab Test · Online",
    offlineLabtestOrders: "Lab Test · In-Store",
    labTestInStore: "Lab Test · In-Store",
    onlineReturns: "Medicine Return · Online",
    offlineReturns: "Medicine Return · In-Store",
    otherExpenses: "Other Expenses",
    bankDeposits: "Bank Deposits",
  };

  if (keyName && keyMap[keyName]) return keyMap[keyName];

  return directName || "Other Expenses";
}

export default function BifurcationSection({ bifurcation, loading }: BifurcationSectionProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.03)]">
        <div className="flex items-center justify-between mb-4">
          <div className="h-5 w-48 bg-gray-200 rounded animate-pulse" />
          <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="border border-gray-100 rounded-xl p-4 bg-white animate-pulse">
              <div className="h-3 w-28 bg-gray-200 rounded mb-2.5" />
              <div className="h-6 w-24 bg-gray-200 rounded mb-3" />
              <div className="h-1.5 w-full bg-gray-100 rounded mb-3" />
              <div className="flex justify-between">
                <div className="h-3 w-12 bg-gray-100 rounded" />
                <div className="h-3 w-12 bg-gray-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!bifurcation) return null;

  const entries = Object.entries(bifurcation);

  return (
    <div className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.03)]">
      {/* Collapsible Header */}
      <div
        className="flex items-center justify-between cursor-pointer select-none"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <h3 className="text-base font-semibold text-gray-800">Breakdown by Category</h3>
        <button
          type="button"
          className="p-1 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
          aria-label={isCollapsed ? "Expand category breakdown" : "Collapse category breakdown"}
        >
          {isCollapsed ? (
            <ChevronDown className="w-5 h-5" />
          ) : (
            <ChevronUp className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Category Cards Grid */}
      {!isCollapsed && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3.5 mt-4">
          {entries.map(([key, item], idx) => {
            const orderType = item?.orderType || (item as any)?.order_type || "";
            const name = getCategoryName(item, key);
            const totalAmount = Number(item?.totalAmount ?? (item as any)?.total_amount ?? 0);
            const onlineAmount = Number(item?.onlineAmount ?? (item as any)?.online_amount ?? 0);
            const cashAmount = Number(item?.cashAmount ?? (item as any)?.cash_amount ?? 0);

            // Compute percentage split for dual colored bar
            const sumSplit = onlineAmount + cashAmount;
            let onlinePct = 50;
            let cashPct = 50;
            if (sumSplit > 0) {
              onlinePct = Math.round((onlineAmount / sumSplit) * 100);
              cashPct = 100 - onlinePct;
            } else if (totalAmount > 0) {
              onlinePct = 50;
              cashPct = 50;
            }

            return (
              <div
                key={orderType || key || idx}
                className="border border-gray-100 rounded-xl p-4 bg-white hover:border-gray-200 transition-colors flex flex-col justify-between"
              >
                <div>
                  <p className="text-xs text-gray-500 font-medium truncate">{name}</p>
                  <p className="text-lg font-bold text-gray-900 mt-1">
                    {formatTotal(totalAmount)}
                  </p>
                </div>

                <div className="mt-3">
                  {/* Dual progress bar: Blue for Online, Orange for Cash */}
                  <div className="w-full h-1.5 bg-gray-100 rounded-full flex overflow-hidden">
                    <div
                      className="h-full bg-[#3B82F6] transition-all duration-300"
                      style={{ width: `${onlinePct}%` }}
                    />
                    <div
                      className="h-full bg-[#F97316] transition-all duration-300"
                      style={{ width: `${cashPct}%` }}
                    />
                  </div>

                  {/* Split stats row */}
                  <div className="flex items-center justify-between mt-2.5">
                    <div>
                      <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                        ONLINE
                      </p>
                      <p className="text-xs font-semibold text-gray-800 mt-0.5">
                        {formatCurrency(onlineAmount)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                        CASH
                      </p>
                      <p className="text-xs font-semibold text-gray-800 mt-0.5">
                        {formatCurrency(cashAmount)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
